import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, ExternalLink, MapPin, PackageCheck,
  Printer, RefreshCw, Truck,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import AdminSearch, { matchesQuery } from '../../components/admin/AdminSearch';
import ConfirmDialog from '../../components/ConfirmDialog';

// Shipped orders never leave the system, so without a cutoff the last lane
// grows forever and the board stops being a work surface. Search overrides it
// (see `filtered`) so an old tracking number is still findable from here.
const SHIPPED_LANE_DAYS = 14;

// The lane an order is REALLY in comes from the timestamps, not from
// fulfillment_status — the timestamps are written by the person doing the
// physical work and match what the packing-bench camera recorded, whereas the
// status string can drift when a webhook fails or a step gets skipped. Order
// matters: first match wins, so the lanes are mutually exclusive.
function laneFor(o) {
  if (o.shipped_at) return 'shipped';
  if (o.sealed_at) return 'ready';
  if (o.label_printed_at) return 'packing';
  if (o.label_url) return 'to_print';
  return 'needs_label';
}

// fulfillment_status is what the rest of the app (customer order pages, emails)
// reads, so when it disagrees with the physical record something upstream broke
// and staff should see it rather than have it silently corrected.
const EXPECTED_STATUS = {
  needs_label: ['unfulfilled'],  // held orders live here until staff charge them
  to_print: ['label_purchased'],
  packing: ['printed'],
  ready: ['sealed'],
  shipped: ['shipped', 'delivered', 'completed'],
};

const LANES = [
  { key: 'needs_label', label: 'Needs label', color: 'var(--danger)', icon: AlertTriangle },
  { key: 'to_print', label: 'To print', color: 'var(--accent)', icon: Printer },
  { key: 'packing', label: 'Packing', color: 'var(--accent)', icon: PackageCheck },
  { key: 'ready', label: 'Ready to drop', color: 'var(--accent)', icon: Truck },
  { key: 'shipped', label: 'Shipped', color: 'var(--success)', icon: CheckCircle2 },
];

function code(id) {
  return id.slice(0, 8).toUpperCase();
}

function money(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

// A Stripe authorization dies after about 7 days. If nobody captures by then
// the hold silently drops off, the shop gets nothing, and the customer has to
// be asked to order again — so the board counts down rather than letting staff
// discover it when a capture fails.
const AUTH_HOLD_DAYS = 7;
const AUTH_WARN_DAYS = 2; // start shouting with this much left

function holdClock(o) {
  if (o.payment_status !== 'authorized') return null;
  const start = o.authorized_at || o.created_at;
  if (!start) return null;
  const endsAt = new Date(start).getTime() + AUTH_HOLD_DAYS * 86400000;
  const msLeft = endsAt - Date.now();
  const daysLeft = Math.floor(msLeft / 86400000);
  const hoursLeft = Math.floor(msLeft / 3600000);
  // Staff kept reading "expires in 3 days" as though the hold were only ever
  // 3 days long. Name the date and the policy so neither is a guess.
  const on = new Date(endsAt).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
  const left = daysLeft >= 1
    ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
    : `${Math.max(hoursLeft, 1)}h left`;
  return {
    expired: msLeft <= 0,
    urgent: msLeft > 0 && daysLeft < AUTH_WARN_DAYS,
    label: msLeft <= 0
      ? `Hold expired ${on} — cannot charge, customer must reorder`
      : `Hold expires ${on} · ${left} of ${AUTH_HOLD_DAYS} days`,
  };
}

function stamp(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// Phone gets a tab strip instead of five squeezed columns. Matches the 768px
// breakpoint the admin shell already flips at in index.css.
function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function TimeRow({ label, value, warn }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, color: warn ? 'var(--danger)' : 'var(--text-muted)' }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{stamp(value)}</span>
    </div>
  );
}

function OrderCard({ order: o, lane, busy, onRetry, onPrint, onSeal, onShip }) {
  const items = o.order_items || [];
  const addr = o.shipping_address || {};
  const prints = o.label_print_count || 0;
  // A reprint after the box was taped shut means someone opened it back up.
  // That is exactly the moment the camera footage needs cross-referencing, so
  // it gets the loudest treatment on the card.
  const repackedAfterSeal = !!(o.sealed_at && o.label_last_printed_at
    && new Date(o.label_last_printed_at) > new Date(o.sealed_at));
  const drifted = !EXPECTED_STATUS[lane].includes(o.fulfillment_status);
  const hold = holdClock(o);

  return (
    <div
      className="sv-card"
      style={{
        padding: 12,
        border: repackedAfterSeal
          ? '1px solid var(--danger)'
          : (lane === 'needs_label' ? '1px solid var(--danger)' : '1px solid var(--border)'),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontWeight: 800, fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}>#{code(o.id)}</span>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{stamp(o.created_at)}</span>
      </div>

      <div style={{ fontSize: '0.84rem', marginTop: 2 }}>{o.guest_name || o.guest_email || 'Account holder'}</div>

      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
        {items.map((it) => (
          <div key={it.id}>{it.quantity}× {it.product_name_snapshot}</div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 8 }}>
        <MapPin size={12} />
        {[addr.city, addr.state].filter(Boolean).join(', ') || 'No address on order'}
      </div>

      {(o.shipping_carrier || o.shipping_service) && (
        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 3 }}>
          {[o.shipping_carrier, o.shipping_service].filter(Boolean).join(' · ')}
        </div>
      )}

      {/* Quoted vs actual postage. They diverge when a stale rate had to be
          re-rated at buy time — UPS voids a rate after 24h — and the shop eats
          the difference. Small, but staff should see it rather than wonder, and
          it is the column a spend report reads. */}
      {o.label_cost_cents != null && (
        <div style={{ fontSize: '0.76rem', marginTop: 3, color: 'var(--text-muted)' }}>
          Postage {money(o.label_cost_cents)}
          {o.shipping_rate_cents != null && o.shipping_rate_cents !== o.label_cost_cents && (
            <>
              {' · quoted '}{money(o.shipping_rate_cents)}
              <span style={{
                marginLeft: 5, fontWeight: 700,
                color: o.label_cost_cents > o.shipping_rate_cents ? 'var(--warning)' : 'var(--success, var(--text-muted))',
              }}>
                {o.label_cost_cents > o.shipping_rate_cents ? '+' : '−'}
                {money(Math.abs(o.label_cost_cents - o.shipping_rate_cents))}
              </span>
            </>
          )}
        </div>
      )}

      {o.tracking_number && (
        <div style={{ fontSize: '0.76rem', marginTop: 3, wordBreak: 'break-all' }}>
          {o.tracking_url
            ? <a href={o.tracking_url} target="_blank" rel="noopener noreferrer">{o.tracking_number}</a>
            : o.tracking_number}
        </div>
      )}

      {lane === 'shipped' && (o.carrier_status || o.delivered_at) && (
        <div style={{ fontSize: '0.76rem', marginTop: 6, color: o.delivered_at ? 'var(--success)' : 'var(--text-muted)' }}>
          {o.delivered_at ? `Delivered ${stamp(o.delivered_at)}` : `Carrier: ${o.carrier_status}`}
        </div>
      )}

      {(o.label_printed_at || o.sealed_at || o.shipped_at) && (
        <div
          style={{
            fontSize: '0.72rem', marginTop: 9, paddingTop: 8,
            borderTop: '1px solid var(--border)', display: 'flex',
            flexDirection: 'column', gap: 2,
          }}
        >
          <TimeRow label="Printed" value={o.label_printed_at} />
          <TimeRow label="Sealed" value={o.sealed_at} />
          <TimeRow label="Shipped" value={o.shipped_at} />
          {prints > 1 && (
            <TimeRow
              label={`Reprint ×${prints - 1}, last`}
              value={o.label_last_printed_at}
              warn={repackedAfterSeal}
            />
          )}
        </div>
      )}

      {repackedAfterSeal && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--danger)', fontWeight: 700, marginTop: 6 }}>
          <AlertTriangle size={12} />
          Reprinted after sealing — box was reopened
        </div>
      )}

      {drifted && (
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 6 }}>
          Status field says “{o.fulfillment_status}”
        </div>
      )}

      {hold && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 5, marginTop: 9,
            paddingTop: 8, borderTop: '1px solid var(--border)',
            fontSize: '0.74rem', fontWeight: 700,
            color: hold.expired || hold.urgent ? 'var(--danger)' : 'var(--warning)',
          }}
        >
          <AlertTriangle size={12} />
          <span>{money(o.total_cents)} held · {hold.label}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {lane === 'needs_label' && (
          // Wired up 2026-08-27 to shinyvault-buy-label. It spends real
          // postage, so the handler confirms before calling.
          <button
            type="button"
            onClick={() => onRetry(o)}
            disabled={busy}
            title={hold
              ? `Charge ${money(o.total_cents)} and buy the shipping label`
              : 'Buy a shipping label for this order now'}
            className="sv-btn"
            style={{ padding: '6px 11px', fontSize: '0.78rem' }}
          >
            {hold ? 'Charge & buy label' : 'Retry label'}
          </button>
        )}

        {o.label_url && (
          <button
            type="button"
            onClick={() => onPrint(o)}
            disabled={busy}
            className={lane === 'to_print' ? 'sv-btn' : 'sv-btn-outline'}
            style={{ padding: '6px 11px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Printer size={13} />
            {o.label_printed_at ? 'Reprint' : 'Print label'}
          </button>
        )}

        {lane === 'packing' && (
          <button
            type="button"
            onClick={() => onSeal(o)}
            disabled={busy}
            className="sv-btn"
            style={{ padding: '6px 11px', fontSize: '0.78rem' }}
          >
            Sealed &amp; ready
          </button>
        )}

        {lane === 'ready' && (
          <button
            type="button"
            onClick={() => onShip(o)}
            disabled={busy}
            className="sv-btn"
            style={{ padding: '6px 11px', fontSize: '0.78rem' }}
          >
            Mark shipped
          </button>
        )}

        {o.label_url && lane === 'needs_label' && (
          <a
            href={o.label_url}
            target="_blank"
            rel="noopener noreferrer"
            className="sv-btn-outline"
            style={{ padding: '6px 11px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}
          >
            <ExternalLink size={13} />
            Label
          </a>
        )}
      </div>
    </div>
  );
}

export default function AdminFulfillmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isNarrow = useIsNarrow();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [activeLane, setActiveLane] = useState('needs_label');
  const [error, setError] = useState('');
  // Things that failed AFTER the status change already saved — currently just
  // the shipped email. Kept apart from `error` so staff don't think the status
  // needs redoing, and shown rather than swallowed: a customer who never got
  // their tracking number is a support ticket nobody saw coming (2026-08-29).
  const [warnings, setWarnings] = useState([]);

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      // 'authorized' as well as 'paid': the card is held and the order is
      // waiting for someone to charge it and buy the label. Filtering to 'paid'
      // alone would make every new order invisible to staff.
      .in('payment_status', ['authorized', 'paid'])
      .eq('fulfillment_method', 'ship')
      .order('created_at', { ascending: true });
    if (err) setError(err.message);
    setOrders(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const cutoff = Date.now() - SHIPPED_LANE_DAYS * 86400000;
    const searching = !!query.trim();
    return orders.filter((o) => {
      if (!searching && o.shipped_at && new Date(o.shipped_at).getTime() < cutoff) return false;
      if (!searching) return true;
      return matchesQuery(query, [
        o.id, o.id?.slice(0, 8),
        o.guest_name, o.guest_email,
        o.tracking_number, o.shipping_carrier, o.shipping_service, o.fulfillment_status,
        o.shipping_address?.name, o.shipping_address?.city,
        o.shipping_address?.state, o.shipping_address?.zip,
        (o.order_items || []).map((it) => it.product_name_snapshot).join(' '),
      ]);
    });
  }, [orders, query]);

  const lanes = useMemo(() => {
    const grouped = { needs_label: [], to_print: [], packing: [], ready: [], shipped: [] };
    filtered.forEach((o) => grouped[laneFor(o)].push(o));
    // Everything else is oldest-first (the thing waiting longest is the thing to
    // do next); shipped reads newest-first because it is a receipt, not a queue.
    grouped.shipped.reverse();
    return grouped;
  }, [filtered]);

  // Buy a label for an order the automatic purchase failed on. Calls the
  // staff-only shinyvault-buy-label function, which spends real postage - so
  // it confirms first, and it shows the carrier's actual refusal message
  // instead of leaving staff staring at a blank order (Chase 2026-08-27).
  const retryLabel = async (o) => {
    const held = o.payment_status === 'authorized';
    if (!window.confirm(held
      ? `Charge ${money(o.total_cents)} and buy the shipping label?\n\n`
        + "This TAKES the customer's money — their card is only on hold right now — "
        + 'and buys postage on the Shippo account. Only do this if you are shipping it.'
      : 'Buy a shipping label for this order now?\n\nThis charges postage to the '
        + 'Shippo account. Only do this if the order really has no label.')) return;
    setBusyId(o.id);
    setError('');
    try {
      // The board renders from a cached user in context, but the access token
      // behind it expires after an hour. A stale token made the function answer
      // "Not signed in" while the page still looked logged in, which is how two
      // paid orders sat unlabelled for three days. Validate against the server
      // before spending money, and send people to log in rather than guess.
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session) {
        setBusyId(null);
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
        return;
      }
      const { data: who, error: whoErr } = await supabase.auth.getUser();
      if (whoErr || !who?.user) {
        setBusyId(null);
        setError('Your session expired. Sending you to the login page.');
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
        return;
      }

      const { data, error: err } = await supabase.functions.invoke('shinyvault-buy-label', {
        body: { order_id: o.id },
      });
      // supabase-js puts a non-2xx body on err.context as an unread Response,
      // NOT on err.context.error. Without reading it, every real reason - "Not
      // signed in", "Staff only", "The card hold has expired" - collapsed into
      // the useless "Edge Function returned a non-2xx status code".
      let reason = data?.error || null;
      if (!reason && err) {
        try {
          const body = await err.context?.json?.();
          reason = body?.error || null;
        } catch (_e) { /* body was not JSON */ }
        reason = reason || err.message;
      }
      if (reason) {
        setError(`Label could not be bought: ${reason}`);
      } else if (data?.label_url) {
        window.open(data.label_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setError(`Label could not be bought: ${e.message}`);
    }
    setBusyId(null);
    load();
  };

  const printLabel = async (o) => {
    // window.open has to happen inside the click, before any await, or the
    // popup blocker eats it.
    window.open(o.label_url, '_blank', 'noopener,noreferrer');
    setBusyId(o.id);
    const now = new Date().toISOString();
    const patch = {
      label_last_printed_at: now,
      label_print_count: (o.label_print_count || 0) + 1,
      handled_by: user?.id || null,
    };
    // First print is the audit anchor — it pairs with a camera frame and must
    // never move, so reprints only touch the "last" columns.
    if (!o.label_printed_at) patch.label_printed_at = now;
    // A reprint of an already-sealed or already-shipped box must not drag the
    // customer-facing status backwards.
    if (!o.sealed_at && !o.shipped_at) patch.fulfillment_status = 'printed';
    const { error: err } = await supabase.from('orders').update(patch).eq('id', o.id);
    if (err) setError(err.message);
    await load();
    setBusyId(null);
  };

  const markSealed = async (o) => {
    setBusyId(o.id);
    const { error: err } = await supabase.from('orders').update({
      sealed_at: new Date().toISOString(),
      fulfillment_status: 'sealed',
      handled_by: user?.id || null,
    }).eq('id', o.id);
    if (err) setError(err.message);
    await load();
    setBusyId(null);
  };

  // Same fire-and-forget contract as AdminOrdersPage: the status change is
  // already saved, so a mail failure must not surface as a failed action. It
  // does get reported as a warning though — silently dropping it meant nobody
  // noticed ShinyVault's Resend sender was unverified until someone happened to
  // read the function logs.
  const sendShippedEmail = (o) => {
    if (!o.tracking_number) return Promise.resolve();
    return supabase.functions
      .invoke('shinyvault-order-email', { body: { type: 'order_shipped', order_id: o.id } })
      .then(({ data, error: mailErr }) => {
        const reason = data?.error?.message || data?.error || mailErr?.message;
        if (reason || data?.ok === false) {
          setWarnings((w) => [...w, `#${code(o.id)} is marked shipped, but the "shipped" email did not send${reason ? ` (${reason})` : ''}. ${o.guest_email || 'The customer'} still needs tracking ${o.tracking_number}.`]);
        }
      })
      .catch((e) => {
        setWarnings((w) => [...w, `#${code(o.id)} is marked shipped, but the "shipped" email did not send (${e.message}).`]);
      });
  };

  const markShipped = async (o) => {
    setBusyId(o.id);
    const { error: err } = await supabase.from('orders').update({
      shipped_at: new Date().toISOString(),
      fulfillment_status: 'shipped',
      handled_by: user?.id || null,
    }).eq('id', o.id);
    if (err) setError(err.message);
    else sendShippedEmail(o);
    await load();
    setBusyId(null);
  };

  const shipAll = async () => {
    const batch = lanes.ready;
    setBulkBusy(true);
    // One timestamp for the whole batch. They physically went into the same
    // carrier hand-off, so a single honest time is more truthful than fifty
    // times that differ by however fast someone tapped.
    const { error: err } = await supabase.from('orders').update({
      shipped_at: new Date().toISOString(),
      fulfillment_status: 'shipped',
      handled_by: user?.id || null,
    }).in('id', batch.map((o) => o.id));
    if (err) setError(err.message);
    else await Promise.allSettled(batch.map(sendShippedEmail));
    await load();
    setBulkBusy(false);
    setConfirmBulk(null);
  };

  if (loading) return <div>Loading…</div>;

  const visibleLanes = isNarrow ? LANES.filter((l) => l.key === activeLane) : LANES;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900, margin: 0 }}>Fulfillment</h1>
        <button
          type="button"
          onClick={load}
          className="sv-btn-outline"
          style={{ padding: '7px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '0 0 6px' }}>
        Paid shipping orders only. Each step stamps a time you can line up against the bench camera.
      </p>
      {/* The packing days belong to whoever is at the bench. Customers never see
          them; they are told 24 to 72 hours, which Tuesday and Friday cover. */}
      <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '0 0 16px' }}>
        Packing days are Tuesday and Friday. Customers are told we ship within 24 to 72 hours.
      </p>

      <AdminSearch
        value={query}
        onChange={setQuery}
        placeholder="Search order #, customer, tracking, product…"
        resultCount={filtered.length}
        totalCount={orders.length}
      />

      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, fontSize: '0.85rem' }}>{error}</div>}

      {warnings.length > 0 && (
        <div
          style={{
            border: '1px solid var(--warning)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
            fontSize: '0.85rem',
            lineHeight: 1.5,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--warning)' }} />
          <div style={{ flex: 1 }}>
            {warnings.map((w, i) => <div key={i} style={{ marginBottom: i === warnings.length - 1 ? 0 : 6 }}>{w}</div>)}
          </div>
          <button
            onClick={() => setWarnings([])}
            className="sv-btn-outline"
            style={{ padding: '4px 10px', fontSize: '0.75rem', flexShrink: 0 }}
          >
            Dismiss
          </button>
        </div>
      )}

      {isNarrow && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6, marginBottom: 12 }}>
          {LANES.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => setActiveLane(l.key)}
              className={activeLane === l.key ? 'sv-btn' : 'sv-btn-outline'}
              style={{ padding: '7px 12px', fontSize: '0.78rem', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {l.label} {lanes[l.key].length}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: 'flex', gap: 14, alignItems: 'flex-start',
          overflowX: isNarrow ? 'visible' : 'auto', paddingBottom: 8,
        }}
      >
        {visibleLanes.map((l) => {
          const items = lanes[l.key];
          const Icon = l.icon;
          return (
            <section
              key={l.key}
              style={{
                flex: isNarrow ? '1 1 auto' : '0 0 288px',
                width: isNarrow ? '100%' : 288,
                minWidth: 0,
              }}
            >
              <header
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8,
                  borderBottom: `2px solid ${l.color}`, marginBottom: 10,
                }}
              >
                <Icon size={15} style={{ color: l.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 800, fontSize: '0.86rem' }}>{l.label}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: '0.86rem', color: 'var(--text-muted)' }}>
                  {items.length}
                </span>
              </header>

              {l.key === 'ready' && items.length > 0 && (
                <button
                  type="button"
                  onClick={() => setConfirmBulk(items.length)}
                  disabled={bulkBusy}
                  className="sv-btn"
                  style={{ padding: '7px 12px', fontSize: '0.78rem', width: '100%', marginBottom: 10 }}
                >
                  Mark all {items.length} as shipped
                </button>
              )}

              {l.key === 'needs_label' && items.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--danger)', marginBottom: 10, lineHeight: 1.5 }}>
                  Paid with no label. Nothing ships until this clears.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((o) => (
                  <OrderCard
                    key={o.id}
                    order={o}
                    lane={l.key}
                    busy={busyId === o.id || bulkBusy}
                    onRetry={retryLabel}
                    onPrint={printLabel}
                    onSeal={markSealed}
                    onShip={markShipped}
                  />
                ))}
                {items.length === 0 && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', padding: '10px 2px' }}>
                    Nothing here.
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {confirmBulk !== null && (
        <ConfirmDialog
          title={`Mark ${confirmBulk} as shipped?`}
          message="They all get the same drop-off time and every customer with a tracking number gets a shipping email. Only do this once the boxes are actually with the carrier."
          confirmLabel="Mark shipped"
          busyLabel="Marking…"
          danger={false}
          busy={bulkBusy}
          onConfirm={shipAll}
          onCancel={() => setConfirmBulk(null)}
        />
      )}
    </div>
  );
}
